/**
 * @file   src\reducers\chatHistory.js
 * @brief  This file is responsible for the full-page chat's conversation
 *         history reducer (list + active conversation)
 */
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchChatConversations,
  fetchChatConversation,
  removeChatConversation,
} from "../actions/chatHistory";

const initialState = {
  conversationsLoading: false,
  conversations: [],
  conversationsError: "",
  // Bumped by removeChatConversation.fulfilled, which mutates `conversations`
  // directly. fetchChatConversations captures this at dispatch time and
  // echoes it back on its fulfilled payload, so a GET response that raced a
  // delete which has since landed can be told apart from a current one and
  // dropped - see the fulfilled case below.
  conversationsVersion: 0,
  // The requestId of the most recent fetchChatConversations dispatch. Two
  // list GETs with no delete in between (e.g. one on mount racing one fired
  // right after the first turn completes) have no ordering guarantee either -
  // without this, the older response landing after the newer one would
  // silently overwrite it, even though conversationsVersion matches on both
  // (a bug caught in review). Same guard pattern as activeMessagesRequestId
  // below, applied to the list thunk instead of the detail thunk.
  conversationsRequestId: null,
  activeConversationId: null,
  activeMessagesLoading: false,
  activeMessages: null,
  activeMessagesError: "",
  // The requestId of the most recent fetchChatConversation dispatch. Selecting
  // conversation A then quickly B fires two thunks; without this, A's
  // fulfilled/rejected can land after B's if A's request happens to resolve
  // slower is not the only risk - it can also land in between B's pending and
  // B's own fulfilled/rejected. Either way, a stale response must never
  // overwrite state a newer request already owns (a bug caught in review).
  activeMessagesRequestId: null,
};

const chatHistory = createSlice({
  name: "chatHistory",
  initialState,
  reducers: {
    // "+ New chat": clears the active conversation so the main panel
    // remounts HelpChatPanel back to its empty state.
    selectNewConversation: (state) => {
      state.activeConversationId = null;
      state.activeMessages = null;
      state.activeMessagesError = "";
      // Invalidates any in-flight fetchChatConversation from a previously
      // selected conversation, so its fulfilled/rejected (landing after this)
      // is recognized as stale and ignored instead of repopulating messages
      // for a conversation the user has already navigated away from.
      state.activeMessagesRequestId = null;
      // The in-flight fetch's own fulfilled/rejected now early-returns
      // (requestId mismatch) before ever reaching its `loading = false`
      // line, so without this, activeMessagesLoading is stuck true forever -
      // permanently hiding the panel behind "Loading conversation..." (a bug
      // caught in review).
      state.activeMessagesLoading = false;
    },
    // Picking a conversation from the history list. Only sets the id here -
    // fetchChatConversation (dispatched alongside this) fills in the
    // messages once the request resolves.
    selectConversation: (state, action) => {
      state.activeConversationId = action.payload;
      state.activeMessages = null;
      state.activeMessagesError = "";
      state.activeMessagesRequestId = null;
      // See selectNewConversation - same stuck-loading risk applies here.
      state.activeMessagesLoading = false;
    },
    // A brand-new conversation just got its real id back from the server
    // (HelpChatPanel's `done` event, via ChatPage's onConversationStarted).
    // Deliberately does NOT touch activeMessages/activeMessagesLoading like
    // selectConversation does - the panel that triggered this is already
    // mid-conversation and must not be treated as switching to a
    // different, unloaded one.
    conversationStarted: (state, action) => {
      state.activeConversationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChatConversations.pending, (state, action) => {
      state.conversationsLoading = true;
      state.conversationsError = "";
      // Recorded so fulfilled/rejected below can tell a stale request (one
      // superseded by a second list fetch dispatched before it resolved)
      // apart from the current one - the same requestId-generation guard
      // fetchChatConversation already uses, applied to the list thunk. This
      // is a different race than conversationsVersion covers: two GETs with
      // no delete in between still have no ordering guarantee, and matching
      // versions alone would let the older one win (a bug caught in review).
      state.conversationsRequestId = action.meta.requestId;
    });
    builder.addCase(fetchChatConversations.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.conversationsRequestId) return;
      state.conversationsLoading = false;
      state.conversationsError = "";
      // Dropped, not applied: this response was captured (see the thunk)
      // before a removeChatConversation that has since bumped the version -
      // applying it would silently resurrect the row that delete just
      // removed (a bug caught in review). The delete's own filter is
      // already authoritative for `conversations` in that case, so nothing
      // is lost by discarding the stale GET.
      if (action.payload?.conversationsVersion !== state.conversationsVersion) {
        return;
      }
      state.conversations = action.payload?.result || [];
    });
    builder.addCase(fetchChatConversations.rejected, (state, action) => {
      if (action.meta.requestId !== state.conversationsRequestId) return;
      // Leaves `conversations` untouched rather than wiping it to `[]`.
      // This thunk is re-dispatched on every rename and every new
      // conversation (ChatPage.js), not just on mount - a transient
      // network blip on one of those refetches must not replace an
      // already-good sidebar with "No chats found," which is
      // indistinguishable from real data loss (a bug caught in review).
      // The initial load still degrades to the empty list it starts
      // with - "No chats found" is only reachable here when there
      // really is nothing to show yet. (A 404 specifically never
      // reaches here - see chatHistoryService.js, which resolves it to
      // an empty result instead of rejecting.)
      state.conversationsLoading = false;
      state.conversationsError = action?.error?.message || "";
    });

    // Optimistic-free removal: the row disappears only once the API has
    // confirmed. Deleting the conversation you are reading also clears the
    // active id, so the thread pane falls back to the empty state rather than
    // showing messages belonging to a conversation that no longer exists.
    builder.addCase(removeChatConversation.fulfilled, (state, action) => {
      const deletedId = action.payload;
      state.conversationsVersion += 1;
      state.conversations = state.conversations.filter(
        (c) => c.conversationId !== deletedId,
      );
      if (state.activeConversationId === deletedId) {
        state.activeConversationId = null;
        state.activeMessages = null;
        state.activeMessagesRequestId = null;
        // See selectNewConversation - same stuck-loading risk applies here.
        state.activeMessagesLoading = false;
      }
    });

    builder.addCase(fetchChatConversation.pending, (state, action) => {
      state.activeMessagesLoading = true;
      state.activeMessagesError = "";
      state.activeMessagesRequestId = action.meta.requestId;
    });
    builder.addCase(fetchChatConversation.fulfilled, (state, action) => {
      // A stale response - e.g. conversation A's fetch resolving after the
      // user has already switched to B - must not overwrite B's state, which
      // by now belongs to a newer requestId.
      if (action.meta.requestId !== state.activeMessagesRequestId) return;
      state.activeMessagesLoading = false;
      const result = action.payload?.result;
      // chatHistoryService.getChatConversation resolves a 404 (not found, or
      // not this user's) to a FULFILLED action with `result: null` rather
      // than rejecting - by design, so a missing conversation doesn't read
      // as a network error. But treating that the same as the "fulfilled
      // with messages" case below turned it into activeMessages: [], which
      // is indistinguishable from a real, empty conversation - HelpChatPanel
      // would mount and start posting turns into an id the user has no
      // access to (a bug caught in review). Route it through the same
      // null-messages/error-set shape as `rejected` below instead.
      if (!result) {
        state.activeMessages = null;
        state.activeMessagesError = "not_found";
        return;
      }
      state.activeMessages = result.messages || [];
      state.activeMessagesError = "";
    });
    builder.addCase(fetchChatConversation.rejected, (state, action) => {
      if (action.meta.requestId !== state.activeMessagesRequestId) return;
      state.activeMessagesLoading = false;
      // Left null (not []) on failure so ChatPage can tell "load failed" apart
      // from "this conversation genuinely has no messages" and show the error
      // instead of silently rendering an empty-looking chat (a bug caught in
      // review).
      state.activeMessages = null;
      // Defaulted rather than left as "" so a rejection carrying no message
      // (e.g. a bare `Error()`, or a non-Error rejection) still produces a
      // truthy value here. ChatPage derives showConversationError from this
      // string's truthiness; an empty string is falsy, so it fell through
      // past both the loading and error branches into rendering a live
      // HelpChatPanel over a conversation whose history actually failed to
      // load (a bug caught in review) - the same shape "not_found" above
      // already relies on being truthy.
      state.activeMessagesError = action?.error?.message || "load_failed";
    });
  },
});

export const { selectNewConversation, selectConversation, conversationStarted } =
  chatHistory.actions;

export default chatHistory.reducer;
